// src/oura/board/model.ts
//
// The words and sums behind the Tracker (Delivery board): every client's Delivery,
// read once, added up across clients. Nothing here is new data: each count
// comes from the deliverables the client's own Delivery tab shows, so the
// board and the tab never disagree.
//
// A client's state, by one rule:
//   On track  no deliverable past its date
//   Slipping  1 or 2 of ours past their date
//   At risk   3 or more past their date, or one past its date waiting on the client
//   Not started  the plan has no dates yet

import type { Deliverable, DeliveryGroup, DeliveryPayload } from "@service/aeo/delivery";
import { addDays } from "@service/aeo/clientDocsRules";
import { dayLabel } from "@service/aeo/delivery";
import type { Tone } from "@oura/ui";
import { plural } from "@oura/docs";

/* ------------------------------------------------------------------------- *
 * The client, as the board reads it
 * ------------------------------------------------------------------------- */

/** The few fields of a deliverable the board reads. */
export interface BoardItem {
    key: string;
    code: string;
    label: string;
    month: number;
    due: string | null;
    group: DeliveryGroup;
    /** The next step, in staff words. */
    next: string | null;
    owner: string;
    /** Left out of "N of M done", as on the Delivery tab. */
    byHand: boolean;
}

export interface BoardClient {
    id: string;
    name: string;
    phase: DeliveryPayload["phase"];
    /** "Foundation, month 1" or "Month 2". */
    monthLabel: string;
    termMonths: number;
    items: BoardItem[];
    lastCheck: string | null;
}

/** "Us" for the account manager when no name is set: the board is read by staff. */
function ownerOf(d: Deliverable, manager: string | null | undefined): string {
    const a = d.actor;
    if (!a) return d.group === "waiting_client" ? "Client" : manager || "Us";
    if (a.who === "client") return "Client";
    if (a.who === "oura_check") return "Oura re-check";
    return a.name || manager || "Us";
}

export function boardClientOf(p: DeliveryPayload): BoardClient {
    return {
        id: p.businessId,
        name: p.businessName,
        phase: p.phase,
        monthLabel: p.monthLabel,
        termMonths: p.termMonths,
        lastCheck: p.lastCheck.at,
        items: p.deliverables.map((d) => ({
            key: d.key,
            code: d.code,
            label: d.label,
            month: d.month,
            due: d.due,
            group: d.group,
            next: d.next?.text ?? null,
            owner: ownerOf(d, p.accountManager),
            byHand: d.reportedByHand,
        })),
    };
}

/* ------------------------------------------------------------------------- *
 * Dates
 * ------------------------------------------------------------------------- */

/** Sunday of the week `ymd` falls in (weeks run Monday to Sunday). */
export function weekEnd(ymd: string): string {
    const [y, m, d] = ymd.split("-").map(Number);
    const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay(); // 0 = Sunday
    return addDays(ymd, dow === 0 ? 0 : 7 - dow);
}

function daysBetween(from: string, to: string): number {
    return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}

/* ------------------------------------------------------------------------- *
 * One client's card
 * ------------------------------------------------------------------------- */

export type Health = "at_risk" | "slipping" | "on_track" | "not_started";

export const HEALTH: Record<Health, { label: string; tone: Tone; rank: number }> = {
    at_risk: { label: "At risk", tone: "red", rank: 0 },
    slipping: { label: "Slipping", tone: "amber", rank: 1 },
    on_track: { label: "On track", tone: "green", rank: 2 },
    not_started: { label: "Not started", tone: "gray", rank: 3 },
};

export interface ClientCard {
    client: BoardClient;
    health: Health;
    done: number;
    total: number;
    late: BoardItem[];
    lateOnClient: number;
    waiting: number;
    /** The next date with open work, and how much is open on it. */
    nextGate: { due: string; open: BoardItem[]; monthLabel: string } | null;
    /** The term's last date: the last deliverable's. */
    ends: string | null;
    daysLeft: number | null;
    notes: CardNote[];
    /** Each month of the term, for the strip on the row. */
    months: MonthCell[];
    /** The person who owns most of the client's work, when one does. */
    manager: string | null;
}

export type MonthState = "done" | "late" | "now" | "later";

export interface MonthCell {
    month: number;
    state: MonthState;
    done: number;
    total: number;
    due: string | null;
}

const NOT_PEOPLE = new Set(["Client", "Oura re-check", "Us"]);

/** The term month by month: done, late (something past its date), now (the first month still open), or later. */
function monthsOf(client: BoardClient, today: string): MonthCell[] {
    const cells: MonthCell[] = [];
    let nowSet = false;
    for (let m = 1; m <= client.termMonths; m++) {
        const items = client.items.filter((i) => i.month === m && !i.byHand);
        const done = items.filter((i) => i.group === "done").length;
        const due = items.map((i) => i.due).filter((d): d is string => d !== null).sort()[0] ?? null;
        let state: MonthState;
        if (items.length > 0 && done === items.length) state = "done";
        else if (items.some((i) => isOpen(i) && i.due !== null && i.due < today)) state = "late";
        else if (!nowSet && client.phase !== "not_started") state = "now";
        else state = "later";
        if (state === "now" || state === "late") nowSet = true;
        cells.push({ month: m, state, done, total: items.length, due });
    }
    return cells;
}

function managerOf(client: BoardClient): string | null {
    const counts = new Map<string, number>();
    for (const i of client.items) if (!NOT_PEOPLE.has(i.owner)) counts.set(i.owner, (counts.get(i.owner) ?? 0) + 1);
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;
}

const isOpen = (i: BoardItem) => i.group !== "done";

export function cardOf(client: BoardClient, today: string): ClientCard {
    const counted = client.items.filter((i) => !i.byHand);
    const done = counted.filter((i) => i.group === "done").length;
    const late = client.items.filter((i) => isOpen(i) && i.due !== null && i.due < today);
    const lateOnClient = late.filter((i) => i.group === "waiting_client").length;
    const waiting = client.items.filter((i) => i.group === "waiting_client").length;

    const dates = client.items.map((i) => i.due).filter((d): d is string => d !== null).sort();
    const ends = dates.length ? dates[dates.length - 1] : null;

    const upcoming = client.items.filter((i) => isOpen(i) && i.due !== null && i.due >= today);
    const gateDue = upcoming.map((i) => i.due as string).sort()[0];
    const gateOpen = gateDue ? upcoming.filter((i) => i.due === gateDue) : [];
    const gateMonth = gateOpen[0]?.month;

    const health: Health =
        client.phase === "not_started" || dates.length === 0
            ? "not_started"
            : late.length >= 3 || lateOnClient > 0
              ? "at_risk"
              : late.length > 0
                ? "slipping"
                : "on_track";

    return {
        client,
        health,
        done,
        total: counted.length,
        late,
        lateOnClient,
        waiting,
        nextGate: gateDue ? { due: gateDue, open: gateOpen, monthLabel: gateMonth === 1 ? "Foundation" : `Month ${gateMonth}` } : null,
        ends,
        daysLeft: ends ? Math.max(0, daysBetween(today, ends)) : null,
        notes: notesOf(client, health, late, gateDue, gateOpen, today),
        months: monthsOf(client, today),
        manager: managerOf(client),
    };
}

/** One of the 3 columns of an open client row: a status chip, a short fact, the deliverables it is about. */
export interface CardNote {
    key: string;
    chip: { label: string; tone: Tone };
    text: string;
    refs: BoardItem[];
}

const byDue = (a: BoardItem, b: BoardItem) => (a.due ?? "9999").localeCompare(b.due ?? "9999") || a.code.localeCompare(b.code);

/**
 * The card's lines, from the facts on it. Facts only: no promise of work to
 * come (the board is read as a record).
 */
function notesOf(client: BoardClient, health: Health, late: BoardItem[], gateDue: string | undefined, gateOpen: BoardItem[], today: string): CardNote[] {
    const started = health !== "not_started";
    const ours = late.filter((i) => i.group !== "waiting_client").sort(byDue);
    const waiting = client.items.filter((i) => i.group === "waiting_client").sort(byDue);
    const lateOnes = waiting.filter((i) => i.due !== null && i.due < today).length;
    return [
        ours.length > 0
            ? { key: "late", chip: { label: "Past its date", tone: "red" }, text: `${plural(ours.length, "of ours", "of ours")}, oldest by ${plural(daysBetween(ours[0].due as string, today), "day")}`, refs: ours }
            : { key: "late", chip: { label: "Past its date", tone: "gray" }, text: started ? "None of ours" : "No dates yet", refs: [] },
        waiting.length > 0
            ? {
                  key: "waiting",
                  chip: { label: "Waiting on client", tone: "amber" },
                  text: lateOnes > 0 ? `${lateOnes} of ${waiting.length} past ${lateOnes === 1 ? "its date" : "their dates"}` : `${waiting.length} open`,
                  refs: waiting,
              }
            : { key: "waiting", chip: { label: "Waiting on client", tone: "gray" }, text: "Nothing", refs: [] },
        gateDue
            ? { key: "next", chip: { label: "Next date", tone: "blue" }, text: `${gateOpen.length} open, ${dayLabel(gateDue)}`, refs: [...gateOpen].sort(byDue) }
            : { key: "next", chip: { label: "Next date", tone: "gray" }, text: started ? "Nothing open" : "Once the plan starts", refs: [] },
    ];
}

export function sortCards(cards: ClientCard[]): ClientCard[] {
    return [...cards].sort((a, b) => HEALTH[a.health].rank - HEALTH[b.health].rank || b.late.length - a.late.length || a.client.name.localeCompare(b.client.name));
}

/* ------------------------------------------------------------------------- *
 * Open work, across clients
 * ------------------------------------------------------------------------- */

export type When = "overdue" | "today" | "week" | "next_week";
export type WhenFilter = "all" | When;

export const WHEN: Record<When, { label: string; tone: Tone }> = {
    overdue: { label: "Overdue", tone: "red" },
    today: { label: "Today", tone: "blue" },
    week: { label: "This week", tone: "gray" },
    next_week: { label: "Next week", tone: "gray" },
};
export const WHEN_ORDER: When[] = ["overdue", "today", "week", "next_week"];

export const GROUP_WORD: Record<DeliveryGroup, { label: string; tone: Tone }> = {
    needs_us: { label: "Needs us", tone: "blue" },
    waiting_client: { label: "Waiting on client", tone: "amber" },
    checking: { label: "Checking", tone: "violet" },
    later: { label: "Not started", tone: "gray" },
    done: { label: "Done", tone: "green" },
};

export interface WorkRow {
    client: BoardClient;
    item: BoardItem;
    when: When;
}

export function openWork(clients: BoardClient[], today: string): { rows: WorkRow[]; until: string } {
    const thisWeek = weekEnd(today);
    const until = addDays(thisWeek, 7);
    const rows: WorkRow[] = [];
    for (const client of clients) {
        for (const item of client.items) {
            if (!isOpen(item) || item.due === null || item.due > until) continue;
            const when: When = item.due < today ? "overdue" : item.due === today ? "today" : item.due <= thisWeek ? "week" : "next_week";
            rows.push({ client, item, when });
        }
    }
    rows.sort((a, b) => (a.item.due as string).localeCompare(b.item.due as string) || a.client.name.localeCompare(b.client.name) || a.item.code.localeCompare(b.item.code));
    return { rows, until };
}

/* ------------------------------------------------------------------------- *
 * The headline
 * ------------------------------------------------------------------------- */

export interface Totals {
    done: number;
    total: number;
    active: number;
    atRisk: ClientCard[];
    late: number;
    lateOnClient: number;
    dueToday: number;
    dueThisWeek: number;
    weekEnds: string;
    sentence: string;
}

export function totalsOf(cards: ClientCard[], rows: WorkRow[], today: string): Totals {
    const active = cards.filter((c) => c.health !== "not_started");
    const atRisk = active.filter((c) => c.health === "at_risk");
    const late = active.reduce((n, c) => n + c.late.length, 0);
    const lateOnClient = active.reduce((n, c) => n + c.lateOnClient, 0);
    const dueToday = rows.filter((r) => r.when === "today").length;
    const dueThisWeek = rows.filter((r) => r.when === "week").length;
    const names = atRisk.map((c) => c.client.name);
    const riskText =
        atRisk.length === 0
            ? `None of ${plural(active.length, "active client")} ${active.length === 1 ? "is" : "are"} at risk.`
            : `${atRisk.length} of ${plural(active.length, "active client")} ${atRisk.length === 1 ? "is" : "are"} at risk (${names.length > 2 ? `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}` : names.join(" and ")}).`;
    const lateText = late === 0 ? "Nothing is past its date." : `${plural(late, "deliverable")} ${late === 1 ? "is" : "are"} past ${late === 1 ? "its date" : "their dates"}${lateOnClient > 0 ? `, ${lateOnClient} of them waiting on the client` : ""}.`;
    return {
        done: active.reduce((n, c) => n + c.done, 0),
        total: active.reduce((n, c) => n + c.total, 0),
        active: active.length,
        atRisk,
        late,
        lateOnClient,
        dueToday,
        dueThisWeek,
        weekEnds: weekEnd(today),
        sentence: `${riskText} ${lateText}`,
    };
}

/* ------------------------------------------------------------------------- *
 * The day strip over open work
 * ------------------------------------------------------------------------- */

export interface DayCell {
    /** "overdue", or the day's `YYYY-MM-DD`. */
    key: string;
    count: number;
    lateOnClient: number;
}

/** Past its date, then every working day from today to `until`, with how much is due on each. */
export function dayStrip(rows: WorkRow[], today: string, until: string): DayCell[] {
    const overdue = rows.filter((r) => r.when === "overdue");
    const cells: DayCell[] = [{ key: "overdue", count: overdue.length, lateOnClient: overdue.filter((r) => r.item.group === "waiting_client").length }];
    for (let d = today; d <= until; d = addDays(d, 1)) {
        const dow = new Date(`${d}T00:00:00Z`).getUTCDay();
        const on = rows.filter((r) => r.item.due === d);
        if ((dow === 0 || dow === 6) && on.length === 0) continue;
        cells.push({ key: d, count: on.length, lateOnClient: 0 });
    }
    return cells;
}

/** Each client's count among some rows, most first. */
export function clientsIn(rows: WorkRow[]): { client: BoardClient; count: number }[] {
    const by = new Map<string, { client: BoardClient; count: number }>();
    for (const r of rows) {
        const e = by.get(r.client.id) ?? { client: r.client, count: 0 };
        e.count++;
        by.set(r.client.id, e);
    }
    return Array.from(by.values()).sort((a, b) => b.count - a.count || a.client.name.localeCompare(b.client.name));
}

/* ------------------------------------------------------------------------- *
 * The month calendar
 * ------------------------------------------------------------------------- */

/** "2026-10" to its weeks, Monday first, each day a `YYYY-MM-DD` (days of the months either side included). */
export function monthWeeks(ym: string): string[][] {
    const first = `${ym}-01`;
    const dow = (new Date(`${first}T00:00:00Z`).getUTCDay() + 6) % 7; // 0 = Monday
    let d = addDays(first, -dow);
    const weeks: string[][] = [];
    do {
        const week: string[] = [];
        for (let i = 0; i < 7; i++) {
            week.push(d);
            d = addDays(d, 1);
        }
        weeks.push(week);
    } while (d.slice(0, 7) === ym);
    return weeks;
}

export function shiftMonth(ym: string, n: number): string {
    const [y, m] = ym.split("-").map(Number);
    const t = new Date(Date.UTC(y, m - 1 + n, 1));
    return `${t.getUTCFullYear()}-${String(t.getUTCMonth() + 1).padStart(2, "0")}`;
}

/** Every deliverable dated on a day, done or not, as work rows. */
export function workOn(clients: BoardClient[], ymd: string, today: string): WorkRow[] {
    const rows: WorkRow[] = [];
    for (const client of clients)
        for (const item of client.items) {
            if (item.due !== ymd) continue;
            const when: When = isOpen(item) && ymd < today ? "overdue" : ymd === today ? "today" : ymd <= weekEnd(today) ? "week" : "next_week";
            rows.push({ client, item, when });
        }
    return rows.sort((a, b) => a.client.name.localeCompare(b.client.name) || a.item.code.localeCompare(b.item.code));
}
